/**
 * AIF-01 — "Edit model" dialog. Patches name / type / description / active
 * via `useUpdateAiModelMutation`. All fields optional on the wire; we send
 * the changed values.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import type { AiFindingModel, AiFindingModelType } from "@aeriosiq/types";
import { useUpdateAiModelMutation } from "./aiModelQueries.js";
import { conflictMessage, errStatus } from "./apiError.js";
import { MODEL_TYPE_OPTIONS } from "./modelType.js";

export interface EditModelDialogProps {
  open: boolean;
  model: AiFindingModel;
  onClose: () => void;
  onSaved: () => void;
}

export function EditModelDialog({
  open,
  model,
  onClose,
  onSaved,
}: EditModelDialogProps): ReactElement {
  const [name, setName] = useState(model.modelName);
  const [type, setType] = useState<AiFindingModelType>(model.modelType);
  const [description, setDescription] = useState(model.description ?? "");
  const [isActive, setIsActive] = useState(model.isActive);
  const update = useUpdateAiModelMutation(model.modelId);

  const nameValid = name.trim().length > 0 && name.trim().length <= 100;
  const canSubmit = nameValid && !update.isPending;

  function handleSubmit(): void {
    if (!canSubmit) return;
    update.mutate(
      {
        modelName: name.trim(),
        modelType: type,
        description: description.trim().length > 0 ? description.trim() : null,
        isActive,
      },
      {
        onSuccess: () => {
          onSaved();
        },
      },
    );
  }

  const errorMsg = update.isError
    ? errStatus(update.error) === 409
      ? conflictMessage(update.error, "Could not save the model.")
      : "Could not save the model."
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit AI model</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: 100 } }}
            error={!nameValid}
          />
          <TextField
            select
            label="Type"
            value={type}
            onChange={(e) => {
              setType(e.target.value as AiFindingModelType);
            }}
            fullWidth
          >
            {MODEL_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => {
                  setIsActive(e.target.checked);
                }}
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
